import { useState, useEffect, useCallback } from "react";
import { Building2, Plus, Trash2, Loader2, Users, ArrowLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { AppHeader } from "@/components/AppHeader";
import { TeamManager } from "@/components/TeamManager";
import { WebhookConfigDialog } from "@/components/WebhookConfigDialog";
import { toast } from "sonner";
import { listClinics, createClinic, deleteClinic, errMsg } from "@/api";

export default function SuperAdminDashboard() {
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    try { setClinics(await listClinics()); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setCreating(true);
    try { await createClinic(name.trim()); toast.success("Clínica creada"); setName(""); load(); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setCreating(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`¿Eliminar la clínica "${c.name}" y todos sus datos?`)) return;
    try { await deleteClinic(c.id); toast.success("Clínica eliminada"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (selected) {
    return (
      <div className="max-w-4xl mx-auto px-4 pb-24">
        <AppHeader><WebhookConfigDialog /></AppHeader>
        <Button variant="ghost" data-testid="clinic-back-button" onClick={() => { setSelected(null); load(); }} className="rounded-full text-slate-500 mb-3">
          <ArrowLeft className="w-4 h-4" /> Clínicas
        </Button>
        <div className="flex items-center gap-2 mb-5">
          <Building2 className="w-5 h-5 text-brand-teal" />
          <h2 className="text-2xl font-semibold text-brand-navy">{selected.name}</h2>
        </div>
        <TeamManager clinicId={selected.id} roles={["clinic_admin", "therapist"]} title="Usuarios de la clínica" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 pb-24">
      <AppHeader><WebhookConfigDialog /></AppHeader>
      <div className="flex items-center gap-2 text-brand-teal text-xs font-bold uppercase tracking-[0.2em] mb-3">
        <Building2 className="w-4 h-4" /> Clínicas (multi-tenant)
      </div>

      <form onSubmit={add} className="flex gap-2 mb-6">
        <Input data-testid="clinic-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nombre de la nueva clínica" className="h-11" />
        <Button type="submit" data-testid="create-clinic-button" disabled={creating} className="rounded-full bg-brand-navy hover:bg-brand-navy/90 text-white h-11 px-5 shrink-0">
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Crear
        </Button>
      </form>

      {loading ? (
        <div className="py-10 text-center"><Loader2 className="w-6 h-6 animate-spin text-brand-teal mx-auto" /></div>
      ) : (
        <div className="grid grid-cols-1 gap-3" data-testid="clinics-list">
          {clinics.map((c) => (
            <div key={c.id} data-testid={`clinic-row-${c.id}`} className="bg-white border border-slate-200 rounded-2xl shadow-sm p-5 flex items-center justify-between gap-3">
              <button className="flex items-center gap-4 text-left flex-1" data-testid={`open-clinic-${c.id}`} onClick={() => setSelected(c)}>
                <div className="w-11 h-11 rounded-xl bg-brand-navy flex items-center justify-center text-white"><Building2 className="w-5 h-5" /></div>
                <div>
                  <p className="font-medium text-brand-navy text-lg">{c.name}</p>
                  <div className="flex gap-2 mt-1 flex-wrap">
                    <Badge className="bg-slate-100 text-slate-600 border-0">{c.admins} admin{c.admins === 1 ? "" : "s"}</Badge>
                    <Badge className="bg-slate-100 text-slate-600 border-0"><Users className="w-3 h-3 mr-1" /> {c.therapists} terapeuta{c.therapists === 1 ? "" : "s"}</Badge>
                    <Badge className="bg-brand-teal/10 text-brand-teal border-0">{c.patients} paciente{c.patients === 1 ? "" : "s"}</Badge>
                  </div>
                </div>
              </button>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" data-testid={`delete-clinic-${c.id}`} onClick={() => remove(c)} className="rounded-full border-slate-300 text-red-500 h-9"><Trash2 className="w-4 h-4" /></Button>
                <Button variant="ghost" size="sm" onClick={() => setSelected(c)} className="rounded-full text-slate-400 h-9"><ChevronRight className="w-5 h-5" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
